// Subscription card component with inline-editable name/description and resubscribe

import React, { useState } from 'react';
import { Pencil, Check, X, CreditCard } from 'lucide-react';
import { api } from '../services/api';
import { openCheckout } from '../services/paddle';
import type { Subscription } from '../types';

interface SubscriptionCardProps {
  subscription: Subscription;
  email: string;
  onUpdated: () => void;
  children: React.ReactNode;
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    active: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    canceled: 'bg-red-500/20 text-red-400 border-red-500/30',
    past_due: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    paused: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  };
  const colorClass = colors[status] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';

  return (
    <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full border ${colorClass}`}>
      {status.replace('_', ' ')}
    </span>
  );
}

export function SubscriptionCard({ subscription, email, onUpdated, children }: SubscriptionCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(subscription.name || '');
  const [editDescription, setEditDescription] = useState(subscription.description || '');
  const [isSaving, setIsSaving] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  const handleEdit = () => {
    setEditName(subscription.name || '');
    setEditDescription(subscription.description || '');
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await api.updateSubscriptionLabel(
        subscription.id,
        editName.trim() || null,
        editDescription.trim() || null
      );
      setIsEditing(false);
      onUpdated();
    } catch {
      alert('Failed to update subscription label.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleResubscribe = async () => {
    setIsCheckingOut(true);
    try {
      const config = await api.getCheckoutConfig(subscription.id);
      openCheckout(config, () => {
        // Reload dashboard after successful checkout
        onUpdated();
      });
    } catch {
      alert('Failed to start checkout. Please try again.');
    } finally {
      setIsCheckingOut(false);
    }
  };

  const displayName = subscription.name || 'Subscription';
  const isCanceledOrExpired = subscription.status === 'canceled' || subscription.status === 'past_due';

  // Determine the date label
  const dateLabel = subscription.status === 'active' ? 'Renews' : 'Expires';

  return (
    <div className="mb-8">
      {/* Subscription Info */}
      <div className="bg-card border border-border rounded-xl p-6 hover:border-accent/50 transition-colors">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <p className="text-sm font-medium text-accent uppercase tracking-wider">Subscription</p>
            <StatusBadge status={subscription.status} />
          </div>
          {!isEditing && (
            <button
              onClick={handleEdit}
              className="text-muted-foreground hover:text-foreground transition-colors p-1"
              title="Edit subscription label"
            >
              <Pencil className="w-4 h-4" />
            </button>
          )}
        </div>

        {isEditing ? (
          <div className="space-y-3 mb-4">
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="Subscription name"
              maxLength={100}
              className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-accent"
            />
            <input
              type="text"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              placeholder="Description (optional)"
              maxLength={500}
              className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-accent"
            />
            <div className="flex gap-2">
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground font-medium px-3 py-1.5 rounded-md hover:bg-primary/90 transition-all text-sm disabled:opacity-50"
              >
                <Check className="w-3.5 h-3.5" />
                {isSaving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={handleCancel}
                disabled={isSaving}
                className="inline-flex items-center gap-1.5 border border-border text-foreground font-medium px-3 py-1.5 rounded-md hover:bg-secondary transition-colors bg-transparent text-sm disabled:opacity-50"
              >
                <X className="w-3.5 h-3.5" />
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <h2 className="text-lg font-semibold mb-1 text-foreground">{displayName}</h2>
            {subscription.description && (
              <p className="text-sm text-muted-foreground mb-3">{subscription.description}</p>
            )}
            {!subscription.description && <div className="mb-3" />}
          </>
        )}

        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            {subscription.currentPeriodEndsAt && (
              <span>
                {dateLabel}:{' '}
                <span className="text-foreground">
                  {new Date(subscription.currentPeriodEndsAt).toLocaleDateString()}
                </span>
              </span>
            )}
          </div>

          {isCanceledOrExpired && subscription.licenseSetId && (
            <button
              onClick={handleResubscribe}
              disabled={isCheckingOut}
              className="inline-flex items-center gap-2 bg-accent text-accent-foreground font-medium px-4 py-2 rounded-md hover:bg-accent/90 transition-all text-sm disabled:opacity-50"
            >
              <CreditCard className="w-4 h-4" />
              {isCheckingOut ? 'Loading...' : 'Resubscribe'}
            </button>
          )}
        </div>

        {/* License key rows */}
        <div className="mt-4 space-y-2">
          {children}
        </div>
      </div>
    </div>
  );
}
